import { ArrowRight } from "lucide-react";
import { useState } from "react";

export default function Hero() {
  const [expanded, setExpanded] = useState(false);

  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative w-full bg-white pt-28 md:pt-36 pb-24 md:pb-32"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 w-full">
        <div className="fade-up text-[11px] uppercase tracking-[0.28em] text-[#6B6B6B] mb-6 font-medium">
          About
        </div>

        <h1
          data-testid="hero-headline"
          className="fade-up fade-up-d1 font-display text-[#0A0A0A] text-[40px] sm:text-[56px] md:text-[72px] lg:text-[88px] xl:text-[96px] font-medium leading-[1.02] tracking-[-0.035em]"
        >
          Binaifer Dulani
        </h1>

        <p className="fade-up fade-up-d2 mt-6 max-w-3xl text-[18px] md:text-[22px] text-[#3A3A3A] leading-[1.5] font-normal">
          Award-winning and award-losing Creative. Founding Partner at Talented.
          Coach in training with the International Coaching Federation.
        </p>

        <div className="mt-14 md:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5 reveal">
            <div className="relative w-full aspect-[4/5] overflow-hidden bg-[#F2F0EC] rounded-sm">
              <img
                src="https://customer-assets.emergentagent.com/job_executive-rise/artifacts/hua03qth_Binaifer%20Headshot.jpg"
                alt="Binaifer Dulani"
                data-testid="hero-portrait"
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
          </div>

          <div className="lg:col-span-7 reveal">
            <div className="space-y-4 text-[15px] md:text-[16px] text-[#1F1F1F] leading-[1.6] font-normal max-w-2xl">
              <p>
                In 2024, she was named{" "}
                <span className="text-[#0A0A0A] font-medium">
                  South Asia Creative Leader of the Year
                </span>{" "}
                by Campaign and featured in Impact Magazine's{" "}
                <span className="text-[#0A0A0A] font-medium">
                  50 Most Influential Women in India
                </span>
                .
              </p>

              <div
                data-testid="about-extra"
                className={`grid transition-all duration-500 ease-out ${
                  expanded ? "grid-rows-[1fr] opacity-100 mt-0" : "grid-rows-[0fr] opacity-0"
                }`}
                aria-hidden={!expanded}
              >
                <div className="overflow-hidden">
                  <div className="space-y-4 pt-4">
                    <p>
                      Together with her team, she has built work that is both
                      culturally resonant and commercially effective for some
                      of India's most influential brands.
                    </p>
                    <p>
                      Her portfolio includes advancing progressive womanhood
                      through award-winning campaigns for Tanishq, confronting
                      caste discrimination through Urban Company, preserving
                      the legacies of freedom fighters for Britannia, and
                      helping transform India's #3 travel brand into #1 through
                      enduring memory structures.
                    </p>
                    <p>
                      She has shaped trust-building communication for The
                      Hindu, an Indian institution, and redefined B2B
                      storytelling with Meta.
                    </p>
              <p>
                She also co-runs{" "}
                <a
                  href="https://indiancreativewomen.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-link-icw"
                  className="text-[#0A0A0A] font-medium underline-offset-4 underline decoration-[#C5A880]/60 hover:decoration-[#0A0A0A] transition-colors"
                >
                  Indian Creative Women
                </a>
                , a platform dedicated to closing gender gaps in
                leadership.
              </p>
                    <p>
                      She teaches at leading ad schools across India and
                      regularly serves as a juror at global award shows,
                      including Cannes Lions, Clio Awards, Gerety Awards, One
                      Asia Creative Awards, and The One Show.
                    </p>
                    <p>
                      Outside her advertising career, Binaifer has been
                      training with the{" "}
                      <span className="text-[#0A0A0A] font-medium">
                        International Coaching Federation
                      </span>{" "}
                      and has completed 50 pro-bono hours coaching those on the
                      path of leadership.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <button
              data-testid="about-toggle"
              onClick={() => setExpanded((v) => !v)}
              className="mt-5 inline-flex items-center gap-2 text-[13px] font-medium text-[#0A0A0A] hover:opacity-60 transition-opacity"
              aria-expanded={expanded}
            >
              {expanded ? "Read less" : "Read more"}
              <span
                aria-hidden
                className={`inline-block transition-transform duration-300 ${
                  expanded ? "rotate-180" : ""
                }`}
              >
                ↓
              </span>
            </button>

            <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
              <button
                data-testid="hero-cta-work"
                onClick={() => scrollTo("#services")}
                className="group inline-flex items-center gap-2 rounded-full bg-[#0A0A0A] hover:bg-[#1F1F1F] text-white px-7 py-3.5 text-[14px] font-medium transition-all duration-300"
              >
                Work with me
                <ArrowRight
                  size={15}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </button>
              <button
                data-testid="hero-cta-book"
                onClick={() => scrollTo("#booking")}
                className="group inline-flex items-center gap-2 rounded-full border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white text-[#0A0A0A] px-7 py-3.5 text-[14px] font-medium transition-all duration-300"
              >
                Book a coaching session
                <ArrowRight
                  size={15}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </button>
            </div>

            <div className="mt-14 pt-8 border-t border-black/10 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-8 max-w-3xl">
              <Credit
                slug="campaign-leader"
                top="2024"
                title="South Asia Creative Leader of the Year"
                source="Campaign"
              />
              <Credit
                slug="impact-50"
                top="2024"
                title="Top 50 Most Influential Women in Media"
                source="Impact"
              />
              <Credit
                slug="cannes-lions"
                top="Cannes Lions"
                title="See It Be It Alum"
                source="Online Jury"
              />
              <Credit
                slug="coaching-hours"
                top="50+"
                title="Coaching Hours"
                source="In training with International Coaching Federation"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Credit({ top, title, source, slug }) {
  return (
    <div data-testid={`credit-${slug || top.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="font-display text-[20px] sm:text-[22px] text-[#0A0A0A] font-medium tracking-[-0.02em] leading-[1.15]">
        {top}
      </div>
      <div className="mt-2 text-[13px] text-[#1F1F1F] leading-[1.4] font-medium">
        {title}
      </div>
      {source && (
        <div className="mt-2 text-[10px] uppercase tracking-[0.22em] text-[#6B6B6B] leading-snug font-medium">
          {source}
        </div>
      )}
    </div>
  );
}
